'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: string;
  name: string;
  location: string;
  image: string;
  alt: string;
  rating: number;
  review: string;
  product: string;
}

const TestimonialsSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 't1',
    name: 'Priya Sharma',
    location: 'Mumbai, Maharashtra',
    image: "/assets/images/priya-1766911596797.png",
    alt: 'Smiling Indian woman in her 30s with long black hair wearing traditional red saree',
    rating: 5,
    review: 'The mango pickle tastes exactly like my grandmother used to make! My kids love it and I feel good knowing there are no preservatives. Worth every rupee!',
    product: 'Mango Pickle'
  },
  {
    id: 't2',
    name: 'Rajesh Kumar',
    location: 'Delhi, NCR',
    image: "/assets/images/rajesh-1766911611269.png",
    alt: 'Professional Indian man in his 40s with short black hair wearing blue shirt smiling at camera',
    rating: 5,
    review: 'The deshi ghee is pure gold! You can taste the quality in every spoonful. My family\'s health has improved since we switched from commercial brands.',
    product: 'Pure Deshi Ghee'
  },
  {
    id: 't3',
    name: 'Anjali Patel',
    location: 'Ahmedabad, Gujarat',
    image: "/assets/images/anjali-1766911569263.png",
    alt: 'Happy Indian woman in her 30s with shoulder-length black hair wearing green kurta',
    rating: 5,
    review: 'Finally found authentic murrabas! The amla murraba has helped boost my immunity. The taste reminds me of my childhood. Highly recommended!',
    product: 'Amla Murraba'
  },
  {
    id: 't4',
    name: 'Vikram Singh',
    location: 'Jaipur, Rajasthan',
    image: "/assets/images/vikram-1766911658436.png",
    alt: 'Indian man in his 50s with gray beard wearing white kurta smiling warmly',
    rating: 5,
    review: 'As someone who values traditional food, this is a blessing. The mixed vegetable pickle is outstanding. Great quality and authentic taste!',
    product: 'Mixed Vegetable Pickle'
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, testimonials.length]);

  const handlePrevious = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) =>
    prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const currentTestimonial = isHydrated ?
  testimonials[currentIndex] :
  testimonials[0];

  return (
    <section id="reviews" className="bg-muted py-16 lg:py-24">
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="mb-12 text-center">
          <h2 className="font-heading mb-4 text-3xl font-bold text-foreground lg:text-4xl">
            What Our <span className="text-primary">Customers Say</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Join thousands of satisfied families enjoying authentic traditional foods
          </p>
        </div>

        <div className="relative mx-auto max-w-4xl">
          <div className="overflow-hidden rounded-2xl bg-background p-8 shadow-cta lg:p-12">
            <div className="mb-6 flex items-center gap-6">
              <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-full bg-muted">
                <AppImage
                  src={currentTestimonial.image}
                  alt={currentTestimonial.alt}
                  className="h-full w-full object-cover" />

              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl font-bold text-foreground">
                  {currentTestimonial.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {currentTestimonial.location}
                </p>
                <div className="mt-2 flex gap-1">
                  {Array.from({ length: currentTestimonial.rating }).map(
                    (_, i) =>
                    <Icon
                      key={i}
                      name="StarIcon"
                      size={16}
                      variant="solid"
                      className="text-warning" />


                  )}
                </div>
              </div>
            </div>

            <blockquote className="mb-4 text-lg text-foreground">
              "{currentTestimonial.review}"
            </blockquote>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="ShoppingBagIcon" size={16} className="text-primary" />
              <span>Purchased: {currentTestimonial.product}</span>
            </div>
          </div>

          <button
            onClick={handlePrevious}
            className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary p-3 text-primary-foreground shadow-cta transition-all duration-250 ease-out hover:shadow-lg lg:-translate-x-full"
            aria-label="Previous testimonial">

            <Icon name="ChevronLeftIcon" size={24} />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 rounded-full bg-primary p-3 text-primary-foreground shadow-cta transition-all duration-250 ease-out hover:shadow-lg lg:translate-x-full"
            aria-label="Next testimonial">

            <Icon name="ChevronRightIcon" size={24} />
          </button>

          <div className="mt-6 flex justify-center gap-2">
            {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => isHydrated && setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all duration-250 ease-out ${
              (isHydrated ? currentIndex : 0) === index ?
              'w-8 bg-primary' : 'w-2 bg-border hover:bg-primary/50'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          <div className="rounded-xl bg-background p-6 text-center shadow-cta">
            <div className="font-heading mb-2 text-4xl font-bold text-primary">
              5000+
            </div>
            <p className="text-muted-foreground">Happy Families</p>
          </div>
          <div className="rounded-xl bg-background p-6 text-center shadow-cta">
            <div className="font-heading mb-2 text-4xl font-bold text-primary">
              4.9/5
            </div>
            <p className="text-muted-foreground">Average Rating</p>
          </div>
          <div className="rounded-xl bg-background p-6 text-center shadow-cta">
            <div className="font-heading mb-2 text-4xl font-bold text-primary">
              95%
            </div>
            <p className="text-muted-foreground">Repeat Customers</p>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;